// ===================================
// KAIXO - Info pública de un usuario
// Vista read-only del perfil KAIXO + sección editable "Mis notas"
// que se guarda en la agenda nativa del teléfono.
// ===================================

import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { Icons } from '../Icons/Icons';
import ImageViewer from '../ImageViewer/ImageViewer';
import { getInterestLabel } from '../../constants/interests';
import useModalClose from '../../hooks/useModalClose';
import './UserInfoModal.css';

function UserInfoModal({ user, currentUserLanguage = 'español', onClose }) {
    useModalClose(onClose);
    const [fullImg, setFullImg] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [savedMsg, setSavedMsg] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    // Datos editables que se guardarán en la agenda del teléfono.
    // Pre-rellenamos teléfono con el del perfil (si existe). El nombre va
    // como sugerencia en placeholder.
    const [contactName, setContactName] = useState('');
    const [contactPhone, setContactPhone] = useState((user && user.phone) || '');
    const [contactEmail, setContactEmail] = useState((user && user.email) || '');
    const [contactNotes, setContactNotes] = useState('');

    if (!user) return null;

    const interests = Array.isArray(user.interests) ? user.interests : [];

    const handleSave = async () => {
        const finalName = contactName.trim() || user.name || '';
        if (!finalName) { setErr('El nombre es obligatorio'); return; }
        if (!contactPhone.trim()) { setErr('El teléfono es obligatorio'); return; }

        let ContactsPlugin = null;
        try { ContactsPlugin = require('@capacitor-community/contacts').Contacts; } catch {}
        if (!ContactsPlugin?.createContact) {
            setErr('Esta acción solo funciona en la app móvil.');
            return;
        }
        setBusy(true);
        setErr('');
        try {
            const c = {
                name: { given: finalName },
                phones: [{ number: contactPhone.trim(), isPrimary: true, type: 'mobile' }]
            };
            if (contactEmail.trim()) c.emails = [{ address: contactEmail.trim(), type: 'home' }];
            if (contactNotes.trim()) c.note = contactNotes.trim();
            await ContactsPlugin.createContact({ contact: c });
            setSavedMsg('✓ Guardado en tu agenda');
            setIsEditing(false);
            try { window.dispatchEvent(new CustomEvent('kaixo_contacts_changed')); } catch {}
            setTimeout(() => setSavedMsg(''), 2200);
        } catch (e) {
            setErr('No se pudo guardar: ' + (e?.message || 'error'));
        } finally {
            setBusy(false);
        }
    };

    return ReactDOM.createPortal(
        <div className="uinfo-overlay" onClick={onClose}>
            <ImageViewer src={fullImg} onClose={() => setFullImg(null)} />
            <div className="uinfo-modal" onClick={e => e.stopPropagation()}>
                <button className="uinfo-close" onClick={onClose} aria-label="Cerrar">
                    <Icons.Close size={18} color="white" />
                </button>

                <div
                    className="uinfo-avatar"
                    onClick={() => { if (user.avatar) setFullImg(user.avatar); }}
                    style={user.avatar ? { cursor: 'pointer' } : {}}
                >
                    {user.avatar
                        ? <img src={user.avatar} alt="" />
                        : <span>{(user.name || '?').charAt(0).toUpperCase()}</span>
                    }
                </div>

                <h2 className="uinfo-name">{user.name || '—'}</h2>
                {user.status && <p className="uinfo-status">{user.status}</p>}

                <div className="uinfo-fields">
                    {user.language && (
                        <div className="uinfo-field">
                            <Icons.Globe size={14} />
                            <span>{user.language}{user.region ? ` · ${user.region}` : ''}</span>
                        </div>
                    )}
                    {user.country && (
                        <div className="uinfo-field">
                            <span style={{ fontSize: 14 }}>🌍</span>
                            <span>{user.country}</span>
                        </div>
                    )}
                </div>

                {interests.length > 0 && (
                    <div className="uinfo-interests">
                        {interests.map(key => (
                            <span key={key} className="uinfo-interest-chip">
                                {getInterestLabel(key, currentUserLanguage)}
                            </span>
                        ))}
                    </div>
                )}

                {/* Sección editable: lo que TÚ guardas sobre este contacto en
                    la agenda de tu móvil. Lectura por defecto, pulsa el lápiz
                    para editar y Guardar para escribir en la agenda nativa. */}
                <div className="uinfo-section">
                    <div className="uinfo-section-head">
                        <span className="uinfo-section-title">Mis notas sobre este contacto</span>
                        {!isEditing && (
                            <button
                                type="button"
                                className="uinfo-edit-btn"
                                onClick={() => setIsEditing(true)}
                                aria-label="Editar"
                            >
                                <Icons.Edit size={14} /> Editar
                            </button>
                        )}
                    </div>

                    <div className="uinfo-edit-field">
                        <label>Nombre</label>
                        <input
                            type="text"
                            value={contactName}
                            placeholder={user.name || 'Nombre del contacto'}
                            onChange={e => setContactName(e.target.value)}
                            disabled={!isEditing}
                            maxLength={80}
                        />
                    </div>
                    <div className="uinfo-edit-field">
                        <label>Teléfono</label>
                        <input
                            type="tel"
                            value={contactPhone}
                            /* Placeholder solo en modo edición. En lectura,
                               si no hay phone (típico al abrir el perfil
                               de un miembro de grupo — getGroup no popula
                               phone por privacy), el campo queda vacío
                               sin texto fantasma que parezca un número
                               real. Antes mostraba "+34634123456" gris
                               y los usuarios pensaban que era el número
                               del contacto. */
                            placeholder={isEditing ? '+34 600 000 000' : ''}
                            onChange={e => setContactPhone(e.target.value)}
                            disabled={!isEditing}
                            maxLength={20}
                        />
                    </div>
                    <div className="uinfo-edit-field">
                        <label>Email</label>
                        <input
                            type="email"
                            value={contactEmail}
                            placeholder="opcional"
                            onChange={e => setContactEmail(e.target.value)}
                            disabled={!isEditing}
                            maxLength={120}
                        />
                    </div>
                    <div className="uinfo-edit-field">
                        <label>Notas</label>
                        <textarea
                            value={contactNotes}
                            placeholder="opcional"
                            onChange={e => setContactNotes(e.target.value)}
                            disabled={!isEditing}
                            maxLength={300}
                            rows={2}
                        />
                    </div>

                    {err && <div className="uinfo-edit-err">{err}</div>}
                    {savedMsg && <div className="uinfo-edit-ok">{savedMsg}</div>}

                    {isEditing && (
                        <div className="uinfo-edit-actions">
                            <button
                                type="button"
                                className="uinfo-edit-cancel"
                                onClick={() => { setIsEditing(false); setErr(''); }}
                                disabled={busy}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="uinfo-edit-save"
                                onClick={handleSave}
                                disabled={busy}
                            >
                                {busy ? 'Guardando…' : 'Guardar'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
}

export default UserInfoModal;
